import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { X, Check, Loader2, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { langName } from "../i18n/languages";
import { weekdayLabel } from "../lib/locale";
import i18n from "../i18n";

const EXPS = ["Débutant accepté", "1 à 3 ans", "3 à 5 ans", "5 à 10 ans", "Plus de 10 ans"];
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const defaults = () => ({
  title: "", role_type: "Pharmacien", weekdays: [],
  contract_type: "CDI", work_time: "Temps complet", weekly_hours: 35,
  salary_min: "", salary_max: "",
  bonus_objectives: false, thirteenth_month: false, profit_sharing: false,
  housing_provided: false, housing_contribution: false, company_car: false,
  meal_vouchers: false, mutuelle: false, transport: false,
  flexible_hours: false, saturday_work: false, sunday_work: false,
  night_guards: false, weekend_guards: false, continuous_hours: false,
  softwares: [], experience: "Débutant accepté", skills: [], new_missions: [], languages: ["fr"],
  address: "", notes: "",
});

export default function NewRecruitmentModal({ open, onClose, onCreated, existing = null, onSwitchMode }) {
  const { user } = useAuth();
  const pharmacyAddress = user?.address || "";
  const [form, setForm] = useState(defaults());
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const editing = !!existing;

  useEffect(() => {
    if (open && !ref) api.get("/reference").then(({ data }) => setRef(data));
  }, [open, ref]);

  useEffect(() => {
    if (existing) setForm({ ...defaults(), ...existing, address: pharmacyAddress || existing.address || "" });
    else if (open) setForm({ ...defaults(), address: pharmacyAddress });
  }, [existing, open, pharmacyAddress]);

  if (!open) return null;

  const toggle = (key, val) =>
    setForm((f) => {
      const cur = f[key] || [];
      return { ...f, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });

  const submit = async () => {
    setErr("");
    if (!form.title.trim()) return setErr("Intitulé requis");
    if (!form.address.trim()) return setErr("Adresse requise");
    setBusy(true);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min === "" ? null : parseFloat(form.salary_min),
        salary_max: form.salary_max === "" ? null : parseFloat(form.salary_max),
        weekly_hours: form.weekly_hours === "" ? null : parseFloat(form.weekly_hours),
      };
      const { data } = editing
        ? await api.patch(`/recruitments/${existing.id}`, payload)
        : await api.post("/recruitments", payload);
      onCreated?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const ROLES = ref?.professional_roles || ["Pharmacien"];
  const SOFTWARES = ref?.pharmacy_softwares || [];
  const NEW_MISSIONS = ["Vaccination", "Tests de dépistage (TROD angine)", "Tests de dépistage (TROD cystite)",
    "Prescription", "Entretiens pharmaceutiques",
    "Tests allergies", "Tests cholestérol", "Otoscopie", "Test glycémie"];
  const LANGS = ref?.languages || [];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="new-recruit-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl border border-[#E2E5E0] shadow-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#E2E5E0]">
          <div>
            <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>{editing ? "Modifier le poste" : "Nouveau poste"}</h3>
            <p className="text-xs text-[#6B726A] mt-0.5">CDI, CDD, alternance — proposez un poste à long terme.</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0F2EF] grid place-items-center" data-testid="close-recruit-modal"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 sm:p-8 space-y-7">
          {!editing && onSwitchMode && (
            <Field label="Type de besoin">
              <div className="grid sm:grid-cols-2 gap-3" data-testid="mode-picker">
                <ModeCard selected title="Permanent" desc="Poste durable : CDI, CDD, alternance — jours récurrents." testid="mode-permanent" onClick={() => {}} />
                <ModeCard title="Temporaire" desc="Remplacement ponctuel : dates précises sur un calendrier." testid="mode-temporary" onClick={() => onSwitchMode("temporary")} />
              </div>
            </Field>
          )}

          <Field label="Intitulé du poste">
            <input className="rp-input" value={form.title} data-testid="recruit-title"
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="ex. Pharmacien titulaire CDI 35h" />
          </Field>

          <Field label="Type de poste">
            <Chips options={ROLES} value={form.role_type} onPick={(v) => setForm({ ...form, role_type: v })} testidPrefix="recruit-role" />
          </Field>

          <Field label="Jours de la semaine travaillés">
            <Multi options={WEEKDAYS} value={form.weekdays} onToggle={(v) => toggle("weekdays", v)} testidPrefix="recruit-day" labelFor={(k) => weekdayLabel(k)} />
          </Field>

          <div>
            <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Compétences & logiciels</div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-2">Maîtrise des logiciels *</div>
                <div className="grid grid-cols-2 gap-2" data-testid="recruit-softwares">
                  {SOFTWARES.map((s) => (
                    <label key={s} data-testid={`recruit-sw-${s}`} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input type="checkbox" checked={form.softwares.includes(s)} onChange={() => toggle("softwares", s)} className="accent-[#1A4D2E] w-4 h-4 rounded" />
                      <span className={form.softwares.includes(s) ? "text-[#1f2421]" : "text-[#6B726A]"}>{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-2">Nouvelles missions du pharmacien</div>
                <div className="grid grid-cols-1 gap-2" data-testid="recruit-missions">
                  {NEW_MISSIONS.map((m) => (
                    <label key={m} data-testid={`recruit-skill-${m}`} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input type="checkbox" checked={form.new_missions.includes(m)} onChange={() => toggle("new_missions", m)} className="accent-[#1A4D2E] w-4 h-4 rounded" />
                      <span className={form.new_missions.includes(m) ? "text-[#1f2421]" : "text-[#6B726A]"}>{m}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Field label="Expérience requise"><Chips options={EXPS} value={form.experience} onPick={(v) => setForm({ ...form, experience: v })} testidPrefix="recruit-exp" /></Field>
          <Field label="Langues"><Multi options={LANGS} value={form.languages} onToggle={(v) => toggle("languages", v)} testidPrefix="recruit-lang" labelFor={(c) => langName(c, i18n.language)} /></Field>

          <Section title="Lieu" icon={MapPin}>
            <Field label="Adresse de l'officine">
              <div className="relative">
                <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-[#1A4D2E] pointer-events-none" />
                <input className="rp-input bg-[#F0F2EF] text-[#1f2421] font-medium cursor-not-allowed" style={{ paddingLeft: "2.4rem" }} value={form.address} data-testid="recruit-address"
                  readOnly tabIndex={-1}
                  placeholder="Renseignez l'adresse dans Mon officine" />
              </div>
              <div className="text-[11px] text-[#6B726A] mt-1">Récupérée automatiquement depuis votre profil — non modifiable</div>
            </Field>
            <Field label="Commentaires">
              <textarea rows={2} className="rp-input resize-none" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </Section>

          {err && <div className="text-sm text-[#E3655B] bg-[#FFF4F3] border border-[#E3655B]/20 rounded-xl px-3 py-2">{err}</div>}
        </div>

        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-t border-[#E2E5E0] sticky bottom-0 bg-white rounded-b-3xl">
          <button onClick={onClose} className="text-sm text-[#6B726A] hover:text-[#1f2421]">Annuler</button>
          <button onClick={submit} disabled={busy} data-testid="submit-recruit"
            className="bg-[#1A4D2E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#143C24] flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>

      <style>{`
        .rp-input{width:100%;background:#fff;border:1px solid #E2E5E0;border-radius:.75rem;padding:.55rem .85rem;outline:none;font-size:.9rem}
        .rp-input:focus{border-color:#1A4D2E;box-shadow:0 0 0 3px rgba(26,77,46,.12)}
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{label}</span><div className="mt-2">{children}</div></label>;
}
function ModeCard({ selected, onClick, title, desc, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`text-left p-4 rounded-2xl border-2 transition-all ${
        selected ? "border-[#1A4D2E] bg-[#E8F0EA]" : "border-[#E2E5E0] hover:border-[#1A4D2E]/40"
      }`}>
      <div className="font-medium text-sm flex items-center gap-2 mb-1">
        <span className={`w-4 h-4 rounded-full border-2 ${selected ? "border-[#1A4D2E] bg-[#1A4D2E]" : "border-[#C5CBC2]"}`} />
        {title}
      </div>
      <div className="text-xs text-[#6B726A]">{desc}</div>
    </button>
  );
}
function Section({ title, icon: Icon, children }) {
  return (<div className="border-t border-[#E2E5E0] pt-4 space-y-2.5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#6B726A]"><Icon className="w-3.5 h-3.5" /> {title}</div>
    {children}
  </div>);
}
function Chips({ options, value, onPick, testidPrefix }) {
  return <div className="flex flex-wrap gap-2">{options.map((o) => (
    <button key={o} type="button" data-testid={`${testidPrefix}-${o}`}
      onClick={() => onPick(o)}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === o ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]" : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"}`}>{o}</button>
  ))}</div>;
}
function Multi({ options, value, onToggle, testidPrefix, labelFor }) {
  return <div className="flex flex-wrap gap-2">{options.map((o) => (
    <button key={o} type="button" data-testid={`${testidPrefix}-${o}`}
      onClick={() => onToggle(o)}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value.includes(o) ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]" : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"}`}>{labelFor ? labelFor(o) : o}</button>
  ))}</div>;
}
